import { ITableFilterItem } from '../resources/interfaces';
import moment from 'moment';

export const getFilterValue = (filters: ITableFilterItem[], name: string) => {
  const filter = filters.find(f => f.name === name);
  return filter?.value;
};

export const getFilterValues = (filters: ITableFilterItem[]) => {
  return filters.reduce((acc, filter) => {
    acc[filter.name] = filter.value;
    return acc;
  }, {} as Record<string, any>);
};

export const hasActiveFilters = (filters: ITableFilterItem[]) => {
  return filters.some(filter => {
    if (typeof filter.value === 'object' && filter.value !== null) {
      return filter.value.from || filter.value.to;
    }
    return filter.value !== '' && filter.value !== null && filter.value !== undefined;
  });
};

export const getActiveFilters = (filters: ITableFilterItem[]) => {
  return filters.filter(filter => {
    if (typeof filter.value === 'object' && filter.value !== null) {
      return filter.value.from || filter.value.to;
    }
    return filter.value !== '' && filter.value !== null && filter.value !== undefined;
  });
};

export const resetFilterValues = (filters: ITableFilterItem[]): ITableFilterItem[] => {
  return filters.map(filter => ({
    ...filter,
    value: filter.type === 'dateRange' ? { from: '', to: '' } : '',
    value2: ''
  }));
};

export const updateFilterValue = (
  filters: ITableFilterItem[], 
  name: string, 
  value: any, 
  value2?: string
): ITableFilterItem[] => {
  return filters.map(filter => 
    filter.name === name 
      ? { ...filter, value, value2 }
      : filter
  );
};

export const updateFilterByIndex = (
  filters: ITableFilterItem[], 
  index: number, 
  value: any, 
  value2?: string
): ITableFilterItem[] => {
  const newFilters = [...filters];
  newFilters[index] = { ...newFilters[index], value, value2 };
  return newFilters;
};

// Enhanced filter value rendering
export const renderFilterValue = (filter: ITableFilterItem) => {
  if (!filter) return null;

  // Handle boolean values
  if (typeof filter.value === 'boolean') {
    return filter.value ? 'Yes' : 'No';
  }

  // Handle array values (for multi-select)
  if (Array.isArray(filter.value)) {
    return filter.value.length > 0 ? filter.value.join(', ') : null;
  }

  // Handle simple values (string, number)
  const isSimpleValue = (
    typeof filter.value === 'string' ||
    typeof filter.value === 'number' ||
    typeof filter.value2 === 'string'
  ) && !(filter?.value as any)?.from &&
    !(filter?.value as any)?.to &&
    filter.label;

  if (isSimpleValue) {
    const useValue2Labels = [
      'Insurer',
      'To insurer',
      'Broker',
      'Policy manager',
      'Payment Status',
      'Approved Status',
      'From broker'
    ];

    const shouldUseValue2 = useValue2Labels.includes(String(filter.label));
    const rawValue = shouldUseValue2 ? filter.value2 : filter.value;
    return getFormattedSentence(rawValue);
  }

  // Handle date range
  if (filter?.value?.from) {
    const from = moment(String(filter.value.from)).format('DD-MM-YYYY');
    const to = filter.value.to
      ? ` to ${moment(String(filter.value.to)).format('DD-MM-YYYY')}`
      : '';
    return `${from}${to}`;
  }

  // Handle number range
  if (filter?.value?.min !== undefined || filter?.value?.max !== undefined) {
    const min = filter.value.min !== undefined ? filter.value.min : '';
    const max = filter.value.max !== undefined ? filter.value.max : '';
    if (min && max) return `${min} - ${max}`;
    if (min) return `≥ ${min}`;
    if (max) return `≤ ${max}`;
  }

  return null;
};

// Helper function to format sentences
const getFormattedSentence = (value: any): string => {
  if (!value) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Filter configuration for handling different filter types
export const getFilterConfig = (filterName: string) => {
  const configs: Record<string, { useValue2?: boolean; customHandler?: boolean }> = {
    insurer: { useValue2: true },
    insurer_id: { useValue2: true },
    broker: { useValue2: true },
    broker_id: { useValue2: true },
    manager: { useValue2: true },
    payment_status: { useValue2: true },
    approved_status: { useValue2: true },
    policy_type: { useValue2: true },
    review_stage: { useValue2: true },
    protection_type: { useValue2: true },
    vehicle_usage: { useValue2: true }
  };
  
  return configs[filterName] || {};
};
